import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import { Clipboard, Hourglass, SquareCheckBig, Banknote } from 'lucide-react';

const ImprovedDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success, info } = useNotifications();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/dashboard/stats`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = (path, description) => {
    info(`Navegando a ${description}...`);
    setTimeout(() => {
      window.location.href = path;
    }, 500);
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="text-4xl mb-4"><Hourglass /></div>
        <p>Cargando dashboard...</p>
      </div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="mb-8 bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">
                ¡Bienvenido, {user?.name}! 👋
              </h1>
              <p className="text-gray-600 mt-2 text-lg">
                Gestiona todos tus trámites desde un solo lugar
              </p>
            </div>

            <div className="text-right">
              <span className={`inline-flex items-center px-6 py-3 rounded-full text-lg font-medium ${user?.role === 'admin'
                ? 'bg-red-100 text-red-800'
                : user?.role === 'gestor'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-green-100 text-green-800'
                }`}>
                {user?.role === 'admin' ? (<span><Crown className='mr-1' />Administrador</span>) :
                  user?.role === 'gestor' ? (<span><User className='mr-1' />Gestor</span>) : '👤 Cliente'}
              </span>
            </div>
          </div>
        </div>

        {/* Stats Cards mejoradas */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Trámites Activos"
              value={stats.active_tramites || 0}
              icon={<Clipboard />}
              color="blue"
              subtitle="En proceso"
            />
            <StatCard
              title="Completados"
              value={stats.completed_tramites || 0}
              icon={<SquareCheckBig />}
              color="green"
              subtitle="Finalizados"
            />
            <StatCard
              title="En Proceso"
              value={stats.in_progress_tramites || 0}
              icon={<Hourglass />}
              color="yellow"
              subtitle="Revisión"
            />
            <StatCard
              title="Total Gastado"
              value={`$${stats.total_spent || 0}`}
              icon={<Banknote />}
              color="purple"
              subtitle="MXN"
            />
          </div>
        )}

        {/* Navegación principal robusta */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">

          {/* TODOS los roles */}
          <NavigationCard
            title={(<Clipboard /> + " Mis Solicitudes")}
            description="Ver y gestionar todas mis solicitudes de trámites"
            buttonText="Ver Solicitudes"
            color="blue"
            onClick={() => handleNavigation('/applications', 'Mis Solicitudes')}
          />

          <NavigationCard
            title="📂 Explorar Trámites"
            description="Buscar y solicitar nuevos trámites por categoría"
            buttonText="Ver Categorías"
            color="green"
            onClick={() => handleNavigation('/categorias', 'Categorías')}
          />

          {/* Solo ADMIN */}
          {(user?.role === 'admin' || user?.role === 'superadmin') && (
            <NavigationCard
              title="👑 Panel Admin"
              description="Administrar categorías, trámites y usuarios del sistema"
              buttonText="Ir a Admin"
              color="red"
              onClick={() => handleNavigation('/admin', 'Panel Administrativo')}
            />
          )}

          {/* GESTOR y ADMIN */}
          {(user?.role === 'gestor' || user?.role === 'admin' || user?.role === 'superadmin') && (
            <NavigationCard
              title="👨‍💼 Panel Gestor"
              description="Revisar documentos y gestionar casos asignados"
              buttonText="Ir a Gestor"
              color="purple"
              onClick={() => handleNavigation('/gestor', 'Panel de Gestor')}
            />
          )}

          {/* Testing tools */}
          <NavigationCard
            title="🔧 Tools de Testing"
            description="Herramientas para debugging y testing del sistema"
            buttonText="Testing Tools"
            color="gray"
            onClick={() => handleNavigation('/api-test', 'Testing Tools')}
          />

        </div>

        {/* Status del sistema */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4"><ChartNoAxesColumn /> Estado del Sistema</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600"><SquareCheckBig /></div>
              <div className="text-sm text-green-800">Backend Operativo</div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">🔐</div>
              <div className="text-sm text-blue-800">Auth Funcional</div>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600"><MessageCircle /></div>
              <div className="text-sm text-purple-800">Chat Activo</div>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">📈</div>
              <div className="text-sm text-yellow-800">APIs Estables</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, color, subtitle }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200'
  };

  return (
    <div className={`p-6 rounded-2xl border-2 ${colorClasses[color]} transform hover:scale-105 transition-transform`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-75">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
          <p className="text-xs opacity-60">{subtitle}</p>
        </div>
        <div className="text-4xl">{icon}</div>
      </div>
    </div>
  );
};

const NavigationCard = ({ title, description, buttonText, color, onClick }) => {
  const colorClasses = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    green: 'bg-green-600 hover:bg-green-700',
    purple: 'bg-purple-600 hover:bg-purple-700',
    red: 'bg-red-600 hover:bg-red-700',
    gray: 'bg-gray-600 hover:bg-gray-700'
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
      <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600 mb-6 text-sm leading-relaxed">{description}</p>
      <button
        onClick={onClick}
        className={`w-full ${colorClasses[color]} text-white py-3 px-6 rounded-lg font-medium transition-all duration-300 hover:scale-105`}
      >
        {buttonText}
      </button>
    </div>
  );
};

export default ImprovedDashboard;